import { FontTextBaseBold, FontTextSmNormal, PrimitivesWhite } from '@ambulnz/design-tokens';
import { Box, Typography, keyframes } from '@mui/material';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';
import { drawerWidth } from '../../../theme';

const MagicWandIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M16.15 13.05L14 16.5C13.8167 16.7833 13.5625 16.9 13.2375 16.85C12.9125 16.8 12.7083 16.6083 12.625 16.275L11.925 13.475L5.10001 20.3C4.91668 20.4833 4.68751 20.5792 4.41251 20.5875C4.13751 20.5958 3.90001 20.5 3.70001 20.3C3.51668 20.1167 3.42501 19.8833 3.42501 19.6C3.42501 19.3167 3.51668 19.0833 3.70001 18.9L10.525 12.05L7.72501 11.35C7.39168 11.2667 7.20001 11.0625 7.15001 10.7375C7.10001 10.4125 7.21668 10.1583 7.50001 9.975L10.95 7.85L10.65 3.775C10.6167 3.44167 10.75 3.2 11.05 3.05C11.35 2.9 11.625 2.93333 11.875 3.15L15 5.775L18.775 4.25C19.0917 4.11667 19.3667 4.16667 19.6 4.4C19.8333 4.63333 19.8833 4.90833 19.75 5.225L18.225 9L20.85 12.1C21.0667 12.35 21.1 12.625 20.95 12.925C20.8 13.225 20.5583 13.3583 20.225 13.325L16.15 13.05Z" />
  </svg>
);

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const OverlayContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: drawerWidth,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 101,
  gap: '16px',
});

const IconContainer = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  backgroundColor: '#0D2B59',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${pulse} 2s ease-in-out infinite`,
  boxShadow: '0 4px 12px rgba(13, 43, 89, 0.25)',
});

const ProcessingText = styled(Typography)({
  ...FontTextBaseBold,
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '1.5em',
  color: '#131E29',
  textAlign: 'center',
});

const SubText = styled(Typography)({
  ...FontTextSmNormal,
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '1.4em',
  color: '#6B7280',
  textAlign: 'center',
  maxWidth: '280px',
});

const ProgressBar = styled(Box)({
  width: '200px',
  height: '4px',
  backgroundColor: '#E5E7EB',
  borderRadius: '2px',
  overflow: 'hidden',
  marginTop: '8px',
});

const ProgressFill = styled(Box)({
  width: '100%',
  height: '100%',
  background: 'linear-gradient(90deg, #0D2B59 0%, #3B82F6 50%, #0D2B59 100%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
});

function EligibilityProcessingOverlay({ isVisible = false, isAgentProcessing = false }) {
  if (!isVisible) {
    return null;
  }

  const title = isAgentProcessing ? 'Eligibility Agent is Processing' : 'Applying Eligibility Changes';
  const subtitle = isAgentProcessing
    ? 'Please wait while the eligibility agent checks all payers...'
    : 'Please wait while we update the request with the accepted eligibility data...';

  return (
    <OverlayContainer>
      <IconContainer>
        <MagicWandIcon color={PrimitivesWhite} size={32} />
      </IconContainer>
      <ProcessingText>{title}</ProcessingText>
      <SubText>{subtitle}</SubText>
      <ProgressBar>
        <ProgressFill />
      </ProgressBar>
    </OverlayContainer>
  );
}

EligibilityProcessingOverlay.propTypes = {
  isVisible: PropTypes.bool,
  isAgentProcessing: PropTypes.bool,
};

export default EligibilityProcessingOverlay;
